import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { CompanyApiService } from '@features/company/company-api.service';
import { ToastService } from '@core/services/toast.service';
import { EmployeeApiService } from './employee-api.service';
import {
  LookupResponse,
  PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
  PAGE_REQUEST_DEFAULT_PAGE_SIZE,
  PageRequest,
  normalizePageRequest,
} from '@core/models/common.model';
import {
  CompanyNamedLookupResponse,
  CompanyOrganisationalLevelResponse,
  CompanyResponse,
} from '@features/company/company.model';
import {
  CreateDependantRequest,
  DependantLookupResponse,
  DependantResponse,
  CreateEmployeeRequest,
  EmployeeImportEnqueueResponse,
  EmployeeImportResponse,
  EmployeeLookupResponse,
  EmployeeResponse,
  EmployeeTransactionHistoryResponse,
  UpdateDependantRequest,
  UpdateEmployeeRequest,
} from './employee.model';

export type EmployeeImportStatus = 'idle' | 'uploading' | 'queued' | 'already-queued' | 'failed';

type LastImportRequest = {
  file: File;
  idempotencyKey: string;
};

@Injectable({ providedIn: 'root' })
export class EmployeeStore {
  private readonly employeeApiService = inject(EmployeeApiService);
  private readonly companyApiService = inject(CompanyApiService);
  private readonly toastService = inject(ToastService);

  readonly employees = signal<EmployeeResponse[]>([]);
  readonly employeesSearch = signal('');
  readonly employeesTotalCount = signal(0);
  readonly employeesPageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly employeesPageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly employeesTotalPages = signal(0);
  readonly managerLookupResponses = signal<EmployeeLookupResponse[]>([]);
  readonly employeeLookupResponses = signal<EmployeeLookupResponse[]>([]);
  readonly selectedEmployee = signal<EmployeeResponse | null>(null);
  readonly history = signal<EmployeeTransactionHistoryResponse[]>([]);
  readonly dependantLookupResponses = signal<DependantLookupResponse[]>([]);
  readonly dependants = signal<DependantResponse[]>([]);
  readonly companies = signal<CompanyResponse[]>([]);
  readonly companyDivisions = signal<CompanyNamedLookupResponse[]>([]);
  readonly companyJobTitles = signal<CompanyNamedLookupResponse[]>([]);
  readonly organisationalLevels = signal<CompanyOrganisationalLevelResponse[]>([]);
  readonly importResult = signal<EmployeeImportEnqueueResponse | null>(null);
  readonly importStatus = signal<EmployeeImportStatus>('idle');
  readonly importErrorMessage = signal<string | null>(null);
  readonly imports = signal<EmployeeImportResponse[]>([]);
  readonly importsTotalCount = signal(0);
  readonly importsPageNumber = signal(PAGE_REQUEST_DEFAULT_PAGE_NUMBER);
  readonly importsPageSize = signal(PAGE_REQUEST_DEFAULT_PAGE_SIZE);
  readonly importsTotalPages = signal(0);
  readonly importsLoading = signal(false);
  readonly importsErrorMessage = signal<string | null>(null);
  readonly importDownloadId = signal<string | null>(null);
  readonly importDownloadErrorMessage = signal<string | null>(null);
  readonly importErrorDownloadId = signal<string | null>(null);
  readonly importErrorDownloadErrorMessage = signal<string | null>(null);
  readonly importSummaryDownloadId = signal<string | null>(null);
  readonly importSummaryDownloadErrorMessage = signal<string | null>(null);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly deactivating = signal(false);
  readonly activating = signal(false);
  readonly importing = signal(false);
  readonly managerLookupLoading = signal(false);
  readonly employeeLookupLoading = signal(false);
  readonly dependantLookupLoading = signal(false);
  readonly dependantsLoading = signal(false);
  readonly dependantSaving = signal(false);
  readonly dependantDeactivating = signal(false);
  readonly dependantActivating = signal(false);
  private lastImportRequest: LastImportRequest | null = null;

  loadEmployees(pageRequest?: PageRequest, search?: string | null): void {
    const normalizedPageRequest = normalizePageRequest({
      pageNumber: pageRequest?.pageNumber ?? this.employeesPageNumber(),
      pageSize: pageRequest?.pageSize ?? this.employeesPageSize(),
    });

    if (search !== undefined) {
      this.employeesSearch.set(search?.trim() ?? '');
    }

    this.loading.set(true);
    this.employeeApiService
      .getEmployees(normalizedPageRequest, this.employeesSearch())
      .subscribe({
      next: (employees) => {
        this.employees.set(employees.items);
        this.employeesTotalCount.set(employees.totalCount);
        this.employeesPageNumber.set(employees.pageNumber);
        this.employeesPageSize.set(employees.pageSize);
        this.employeesTotalPages.set(employees.totalPages);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  loadManagerLookupResponses(search?: string | null): void {
    this.managerLookupLoading.set(true);
    this.employeeApiService
      .getEmployeeLookupResponses(search, {
        pageNumber: PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
        pageSize: PAGE_REQUEST_DEFAULT_PAGE_SIZE,
      })
      .subscribe({
        next: (response) => {
          this.managerLookupResponses.set(response.items);
          this.managerLookupLoading.set(false);
        },
        error: () => this.managerLookupLoading.set(false),
      });
  }

  loadEmployeeLookupResponses(search?: string | null): void {
    this.employeeLookupLoading.set(true);
    this.employeeApiService
      .getEmployeeLookupResponses(search, {
        pageNumber: PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
        pageSize: PAGE_REQUEST_DEFAULT_PAGE_SIZE,
      })
      .subscribe({
        next: (response) => {
          this.employeeLookupResponses.set(response.items);
          this.employeeLookupLoading.set(false);
        },
        error: () => this.employeeLookupLoading.set(false),
      });
  }

  loadDependantLookupResponses(search?: string | null, includeInactive = true): void {
    this.dependantLookupLoading.set(true);
    this.employeeApiService
      .getDependantLookupResponses(search, {
        pageNumber: PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
        pageSize: PAGE_REQUEST_DEFAULT_PAGE_SIZE,
      }, includeInactive)
      .subscribe({
        next: (response) => {
          this.dependantLookupResponses.set(response.items);
          this.dependantLookupLoading.set(false);
        },
        error: () => this.dependantLookupLoading.set(false),
      });
  }

  loadEmployee(employeeId: string): void {
    this.loading.set(true);
    this.employeeApiService.getEmployee(employeeId).subscribe({
      next: (employee) => {
        this.selectedEmployee.set(employee);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  loadEmployeeHistory(employeeId: string): void {
    this.employeeApiService.getEmployeeHistory(employeeId).subscribe({
      next: (history) => this.history.set(history),
      error: () => undefined,
    });
  }

  loadDependants(employeeId: string, includeInactive = true): void {
    this.dependantsLoading.set(true);
    this.employeeApiService.getDependants(employeeId, includeInactive).subscribe({
      next: (dependants) => {
        this.dependants.set(dependants);
        this.dependantsLoading.set(false);
      },
      error: () => this.dependantsLoading.set(false),
    });
  }

  loadCompanies(clientId?: string | null): void {
    if (!clientId) {
      this.companies.set([]);
      this.clearCompanyDetails();
      return;
    }
    
    this.companyApiService.getCompanies({ clientId }).subscribe({
      next: (companies) => this.companies.set(companies),
      error: () => undefined,
    });
  }

  loadCompanyDetails(companyId?: string | null): void {
    if (!companyId) {
      this.clearCompanyDetails();
      return;
    }

    this.companyApiService.getCompanyDivisions({ companyId }).subscribe({
      next: (items) => this.companyDivisions.set(items),
      error: () => undefined,
    });

    this.companyApiService.getCompanyJobTitles({ companyId }).subscribe({
      next: (items) => this.companyJobTitles.set(items),
      error: () => undefined,
    });

    this.companyApiService.getCompanyOrganisationalLevels({ companyId }).subscribe({
      next: (items) => this.organisationalLevels.set(items),
      error: () => undefined,
    });
  }

  private clearCompanyDetails(): void {
    this.companyDivisions.set([]);
    this.companyJobTitles.set([]);
    this.organisationalLevels.set([]);
  }

  createEmployee(request: CreateEmployeeRequest, onDone?: (employee: EmployeeResponse) => void): void {
    this.saving.set(true);
    this.employeeApiService.createEmployee(request).subscribe({
      next: (employee) => {
        this.selectedEmployee.set(employee);
        this.employees.set([...this.employees(), employee]);
        this.toastService.success('Employee created successfully');
        this.saving.set(false);
        onDone?.(employee);
      },
      error: () => {
        this.toastService.error('Failed to create employee');
        this.saving.set(false);
      },
    });
  }

  updateEmployee(employeeId: string, request: UpdateEmployeeRequest, onDone?: (employee: EmployeeResponse) => void): void {
    this.saving.set(true);
    this.employeeApiService.updateEmployee(employeeId, request).subscribe({
      next: (employee) => {
        this.selectedEmployee.set(employee);
        this.upsertEmployee(employee);
        this.loadEmployeeHistory(employeeId);
        this.toastService.success('Employee updated successfully');
        this.saving.set(false);
        onDone?.(employee);
      },
      error: () => {
        this.toastService.error('Failed to update employee');
        this.saving.set(false);
      },
    });
  }

  deactivateEmployee(employeeId: string, onDone?: (employee: EmployeeResponse) => void): void {
    this.deactivating.set(true);
    this.employeeApiService.deactivateEmployee(employeeId).subscribe({
      next: (employee) => {
        this.selectedEmployee.set(employee);
        this.upsertEmployee(employee);
        this.loadEmployeeHistory(employeeId);
        this.toastService.success('Employee deactivated successfully');
        this.deactivating.set(false);
        onDone?.(employee);
      },
      error: () => {
        this.toastService.error('Failed to deactivate employee');
        this.deactivating.set(false);
      },
    });
  }

  activateEmployee(employeeId: string, onDone?: (employee: EmployeeResponse) => void): void {
    this.activating.set(true);
    this.employeeApiService.activateEmployee(employeeId).subscribe({
      next: (employee) => {
        this.selectedEmployee.set(employee);
        this.upsertEmployee(employee);
        this.loadEmployeeHistory(employeeId);
        this.toastService.success('Employee activated successfully');
        this.activating.set(false);
        onDone?.(employee);
      },
      error: () => {
        this.toastService.error('Failed to activate employee');
        this.activating.set(false);
      },
    });
  }

  createDependant(employeeId: string, request: CreateDependantRequest, onDone?: (dependant: DependantResponse) => void): void {
    this.dependantSaving.set(true);
    this.employeeApiService.createDependant(employeeId, request).subscribe({
      next: (dependant) => {
        this.dependants.set([...this.dependants(), dependant]);
        this.dependantSaving.set(false);
        onDone?.(dependant);
      },
      error: () => this.dependantSaving.set(false),
    });
  }

  updateDependant(
    employeeId: string,
    dependantId: string,
    request: UpdateDependantRequest,
    onDone?: (dependant: DependantResponse) => void,
  ): void {
    this.dependantSaving.set(true);
    this.employeeApiService.updateDependant(employeeId, dependantId, request).subscribe({
      next: (dependant) => {
        this.upsertDependant(dependant);
        this.dependantSaving.set(false);
        onDone?.(dependant);
      },
      error: () => this.dependantSaving.set(false),
    });
  }

  deactivateDependant(employeeId: string, dependantId: string, onDone?: (dependant: DependantResponse) => void): void {
    this.dependantDeactivating.set(true);
    this.employeeApiService.deactivateDependant(employeeId, dependantId).subscribe({
      next: (dependant) => {
        this.upsertDependant(dependant);
        this.dependantDeactivating.set(false);
        onDone?.(dependant);
      },
      error: () => this.dependantDeactivating.set(false),
    });
  }

  activateDependant(employeeId: string, dependantId: string, onDone?: (dependant: DependantResponse) => void): void {
    this.dependantActivating.set(true);
    this.employeeApiService.activateDependant(employeeId, dependantId).subscribe({
      next: (dependant) => {
        this.upsertDependant(dependant);
        this.dependantActivating.set(false);
        onDone?.(dependant);
      },
      error: () => this.dependantActivating.set(false),
    });
  }

  private upsertEmployee(employee: EmployeeResponse): void {
    this.employees.set(this.employees().map((item) => (item.id === employee.id ? employee : item)));
  }

  private upsertDependant(dependant: DependantResponse): void {
    const exists = this.dependants().some((item) => item.id === dependant.id);
    if (!exists) {
      this.dependants.set([...this.dependants(), dependant]);
      return;
    }

    this.dependants.set(this.dependants().map((item) => (item.id === dependant.id ? dependant : item)));
  }

  importEmployees(file: File, idempotencyKey?: string): void {
    const resolvedIdempotencyKey = idempotencyKey ?? crypto.randomUUID();
    this.lastImportRequest = {
      file,
      idempotencyKey: resolvedIdempotencyKey,
    };

    this.importing.set(true);
    this.importStatus.set('uploading');
    this.importErrorMessage.set(null);
    this.importResult.set(null);

    this.employeeApiService.enqueueEmployeeImport({ file, idempotencyKey: resolvedIdempotencyKey }).subscribe({
      next: (result) => {
        this.importResult.set(result);
        this.importStatus.set(result.alreadyQueued ? 'already-queued' : 'queued');
        if (result.alreadyQueued) {
          this.toastService.warning('This file was already queued for import.');
        } else {
          this.toastService.success(`Import queued (Job ID: ${result.jobId}).`);
        }
        this.loadEmployeeImports({
          pageNumber: PAGE_REQUEST_DEFAULT_PAGE_NUMBER,
          pageSize: this.importsPageSize(),
        });
        this.importing.set(false);
      },
      error: (error: unknown) => {
        this.importStatus.set('failed');
        const message = getImportErrorMessage(error);
        this.importErrorMessage.set(message);
        this.toastService.error(message);
        this.importing.set(false);
      },
    });
  }

  retryLastImport(): void {
    if (!this.lastImportRequest) {
      return;
    }

    this.importEmployees(this.lastImportRequest.file, this.lastImportRequest.idempotencyKey);
  }

  loadEmployeeImports(pageRequest?: PageRequest): void {
    const normalizedPageRequest = normalizePageRequest({
      pageNumber: pageRequest?.pageNumber ?? this.importsPageNumber(),
      pageSize: pageRequest?.pageSize ?? this.importsPageSize(),
    });

    this.importsLoading.set(true);
    this.importsErrorMessage.set(null);

    this.employeeApiService.getEmployeeImports(normalizedPageRequest).subscribe({
      next: (response) => {
        this.imports.set(response.items);
        this.importsTotalCount.set(response.totalCount);
        this.importsPageNumber.set(response.pageNumber);
        this.importsPageSize.set(response.pageSize);
        this.importsTotalPages.set(response.totalPages);
        this.importsLoading.set(false);
      },
      error: (error: unknown) => {
        const message = getImportErrorMessage(error);
        this.importsErrorMessage.set(message);
        this.toastService.error(message);
        this.importsLoading.set(false);
      },
    });
  }

  downloadImportFile(importId: string, fileName: string): void {
    this.importDownloadId.set(importId);
    this.importDownloadErrorMessage.set(null);

    this.employeeApiService.downloadEmployeeImportFile(importId).subscribe({
      next: (blob) => {
        downloadBlob(blob, fileName);
        this.importDownloadId.set(null);
      },
      error: (error: unknown) => {
        const message = getImportErrorMessage(error);
        this.importDownloadErrorMessage.set(message);
        this.toastService.error(message);
        this.importDownloadId.set(null);
      },
    });
  }

  downloadImportErrors(importId: string, originalFileName: string): void {
    this.importErrorDownloadId.set(importId);
    this.importErrorDownloadErrorMessage.set(null);

    this.employeeApiService.downloadEmployeeImportErrors(importId).subscribe({
      next: (blob) => {
        const fileName = `${originalFileName}-errors.xlsx`;
        downloadBlob(blob, fileName);
        this.importErrorDownloadId.set(null);
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          const message = 'No error file generated for this import.';
          this.importErrorDownloadErrorMessage.set(message);
          this.toastService.warning(message);
          this.importErrorDownloadId.set(null);
          return;
        }

        const message = getImportErrorMessage(error);
        this.importErrorDownloadErrorMessage.set(message);
        this.toastService.error(message);
        this.importErrorDownloadId.set(null);
      },
    });
  }

  downloadImportSummary(importId: string, originalFileName: string): void {
    this.importSummaryDownloadId.set(importId);
    this.importSummaryDownloadErrorMessage.set(null);

    this.employeeApiService.downloadEmployeeImportSummary(importId).subscribe({
      next: (blob) => {
        const fileName = `${originalFileName}-summary.xlsx`;
        downloadBlob(blob, fileName);
        this.importSummaryDownloadId.set(null);
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          const message = 'No summary file generated for this import.';
          this.importSummaryDownloadErrorMessage.set(message);
          this.toastService.warning(message);
          this.importSummaryDownloadId.set(null);
          return;
        }

        const message = getImportErrorMessage(error);
        this.importSummaryDownloadErrorMessage.set(message);
        this.toastService.error(message);
        this.importSummaryDownloadId.set(null);
      },
    });
  }
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function getImportErrorMessage(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return 'Import request failed.';
  }

  const body = error.error as unknown;
  if (!body || typeof body !== 'object') {
    return error.message || 'Import request failed.';
  }

  const record = body as Record<string, unknown>;
  const detail = getStringField(record, 'detail', 'Detail');
  if (detail) {
    return detail;
  }

  const validationMessage = getFirstValidationMessage(record);
  if (validationMessage) {
    return validationMessage;
  }

  const title = getStringField(record, 'title', 'Title');
  if (title) {
    return title;
  }

  return error.message || 'Import request failed.';
}

function getStringField(record: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return null;
}

function getFirstValidationMessage(record: Record<string, unknown>): string | null {
  const candidate = record['errors'] ?? record['Errors'];
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  for (const value of Object.values(candidate as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const message = value.find((entry) => typeof entry === 'string' && entry.length > 0);
      if (typeof message === 'string') {
        return message;
      }
      continue;
    }

    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return null;
}
